/**
 * ImportPage (FE-4, PHASE1 §7.1). Three-step file-import flow:
 *   upload → preview (dedup badges + selectable rows) → result.
 * A compact history of recent batches is shown under the upload step. German
 * copy throughout.
 */
import { useState } from 'react';
import { PageHeader } from '../components/layout/PageHeader.tsx';
import { ImportUploadForm } from './import/ImportUploadForm.tsx';
import { ImportPreviewTable } from './import/ImportPreviewTable.tsx';
import { ImportResult } from './import/ImportResult.tsx';
import { useImportHistory } from '../lib/hooks/useImport.ts';
import { useAccountMap } from '../lib/hooks/useAccounts.ts';
import { formatDateTime } from '../lib/format.ts';
import { BANK_PROFILE } from '../lib/labels.ts';
import type { ImportCommitOut, ImportPreviewOut } from '../lib/types.ts';

type Stage =
  | { kind: 'upload' }
  | { kind: 'preview'; preview: ImportPreviewOut }
  | { kind: 'result'; result: ImportCommitOut };

const STATUS_LABEL: Record<string, string> = {
  draft: 'Entwurf',
  committed: 'Importiert',
  discarded: 'Verworfen',
};

export default function ImportPage() {
  const [stage, setStage] = useState<Stage>({ kind: 'upload' });

  return (
    <div>
      <PageHeader title="Import" subtitle="Kontoauszüge als CAMT.053 oder CSV importieren" />

      <div className="px-4 py-6 sm:px-6">
        {stage.kind === 'upload' ? (
          <div className="space-y-10">
            <ImportUploadForm onPreview={(preview) => setStage({ kind: 'preview', preview })} />
            <ImportHistory />
          </div>
        ) : null}

        {stage.kind === 'preview' ? (
          <ImportPreviewTable
            preview={stage.preview}
            onCommit={(result) => setStage({ kind: 'result', result })}
            onDiscard={() => setStage({ kind: 'upload' })}
            onBack={() => setStage({ kind: 'upload' })}
          />
        ) : null}

        {stage.kind === 'result' ? (
          <ImportResult result={stage.result} onNewImport={() => setStage({ kind: 'upload' })} />
        ) : null}
      </div>
    </div>
  );
}

/** Recent import batches. */
function ImportHistory() {
  const historyQuery = useImportHistory();
  const accountMap = useAccountMap();
  const batches = historyQuery.data ?? [];

  if (batches.length === 0) return null;

  return (
    <div>
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
        Letzte Importe
      </h2>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
        {batches.map((batch) => (
          <li
            key={batch.import_id}
            className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm"
          >
            <div className="min-w-0">
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {accountMap.get(batch.account_id)?.name ?? `Konto #${batch.account_id}`}
              </span>
              <span className="ml-2 text-slate-400 dark:text-slate-500">
                {BANK_PROFILE[batch.profile]}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-500">
              <span className="tabular-nums">{formatDateTime(batch.created_at)}</span>
              <span className="tabular-nums">{batch.counts.new} neu</span>
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {STATUS_LABEL[batch.status] ?? batch.status}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
